import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './V2Chat.css';

/**
 * V2Chat — reuses v1's chat DOM structure and CSS classes verbatim so the
 * main chat area looks identical to v1 (same width, typography, input bar).
 *
 * The only additions over v1:
 *   - Streams from /api/v2/chat (SSE tool-use loop)
 *   - "Conversations" drawer (hidden by default, overlay-positioned so it
 *     never affects the main chat area's width)
 *   - File upload to /api/v2/chat multipart
 */

const PRACTICE_AREAS = [
  ['public_finance', 'Public Finance'],
  ['c3', '501(c)(3)'],
  ['combined', 'Combined'],
];
const PRACTICE_LABELS = Object.fromEntries(PRACTICE_AREAS);

const ACCEPT = '.pdf,.txt,.doc,.docx,.xlsx,.xls';

// Plain-text expectation-setting under the effort selector. Standard is the
// default and needs no warning; the two slower levels do. Figures are measured
// on one substantive question: at Maximum, Fable 5.1 took 4.8 min and Opus 5.5
// took 14.9 min, so the warning is model-aware rather than one blunt range.
const EFFORT_HINTS = {
  thorough: () => 'Thorough: may take several minutes.',
  maximum: (model) =>
    model === 'opus55'
      ? 'Maximum: may take 10 to 15+ minutes; Fable 5.1 is usually faster at this level.'
      : model === 'fable51'
        ? 'Maximum: may take 5 to 10 minutes.'
        : 'Maximum: may take several minutes.',
};

// Raw tool names are internal; show what the tool is actually doing.
const TOOL_LABELS = {
  search_general_authorities: 'Searching all authorities',
  search_private_business_use: 'Searching private business use authorities',
  search_private_payments_security_loans: 'Searching private payment & security authorities',
  search_management_contracts: 'Searching management contract authorities',
  search_arbitrage_rebate: 'Searching arbitrage & rebate authorities',
  search_debt_instruments: 'Searching debt instrument authorities',
  search_501c3: 'Searching 501(c)(3) authorities',
  search_by_citation: 'Looking up a citation',
  list_available_authorities: 'Checking what the database holds',
  get_corrections: 'Checking practice rules',
  save_correction: 'Saving a practice rule',
  search_c3_authorities: 'Searching the 501(c)(3) corpus',
  get_c3_authority_by_citation: 'Looking up a 501(c)(3) citation',
  list_c3_authorities: 'Checking the 501(c)(3) corpus',
  get_c3_corrections: 'Checking 501(c)(3) practice rules',
  save_c3_correction: 'Saving a 501(c)(3) practice rule',
};

const toolLabel = (t) => {
  if (!t) return 'Searching';
  if (TOOL_LABELS[t]) return TOOL_LABELS[t];
  // Unknown or pipeline-stage strings ("retrieving authorities") arrive as-is;
  // show them readably rather than as a raw identifier.
  return String(t).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

const V2Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingTool, setThinkingTool] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [model, setModel] = useState('opus55'); // per-query Claude model: 'sonnet5' | 'opus55' | 'fable51'
  const [effort, setEffort] = useState('standard'); // answer effort: 'standard' | 'thorough' | 'maximum'
  // Practice area. 'public_finance' is the default and leaves the request
  // path exactly as it was; 'c3' routes retrieval to the 501(c)(3) corpus.
  // The two corpora are never searched together.
  const [practiceArea, setPracticeArea] = useState('public_finance');

  const textareaRef = useRef(null);

  const streamedRef = useRef('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingTool]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // Practice area is fixed for the life of a conversation: the two corpora are
  // never mixed within one thread, and an answer's authorities only make sense
  // under the mode that produced it. Once a conversation has started, choosing
  // a different area starts a new one.
  const practiceAreaLocked = messages.length > 0;

  const choosePracticeArea = (area) => {
    if (area === practiceArea || isLoading) return;
    if (practiceAreaLocked) {
      const ok = window.confirm(
        `This conversation is in ${PRACTICE_LABELS[practiceArea]} mode. ` +
        `Start a new conversation in ${PRACTICE_LABELS[area]} mode?`,
      );
      if (!ok) return;
      newChat();
    }
    setPracticeArea(area);
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setInput('');
    setAttachedFiles([]);
    setThinkingTool(null);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachedFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachedFile = (idx) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: trimmed },
      { role: 'assistant', content: '', mode: practiceArea },
    ]);
    setInput('');
    setIsLoading(true);
    setThinkingTool(null);

    streamedRef.current = '';
    const fd = new FormData();
    fd.append('message', trimmed);
    fd.append('model', model);
    fd.append('effort', effort);
    fd.append('practice_area', practiceArea);
    if (conversationId) fd.append('conversation_id', conversationId);
    attachedFiles.forEach((f) => fd.append('files', f));
    setAttachedFiles([]);

    try {
      const res = await fetch('/api/v2/chat', { method: 'POST', body: fd });
      if (!res.ok || !res.body) {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: `[Error: ${res.status}]` };
          return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.type === 'thinking') {
              setThinkingTool(evt.tool || 'searching');
            } else if (evt.type === 'keepalive') {
              // Proxy-liveness only; nothing to render.
            } else if (evt.type === 'delta') {
              // Progressive text. The final 'message' event still carries the
              // complete answer and overwrites this, so a dropped delta cannot
              // leave a truncated answer on screen.
              streamedRef.current += evt.text || '';
              setThinkingTool(null);
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: 'assistant', content: streamedRef.current };
                return copy;
              });
            } else if (evt.type === 'message') {
              setThinkingTool(null);
              if (evt.conversation_id) setConversationId(evt.conversation_id);
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: 'assistant', content: evt.content };
                return copy;
              });
            } else if (evt.type === 'error') {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: 'assistant', content: `[Error: ${evt.message}]` };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: `[Network error: ${err.message}]` };
        return copy;
      });
    } finally {
      setIsLoading(false);
      setThinkingTool(null);
    }
  };

  return (
    <>
      {/* ── Practice-area tabs: which body of authority this conversation
             runs against. Separate from the model selector, which stays in
             the input area and changes only how the answer is written. ── */}
      <div className="practice-tabs" role="tablist" aria-label="Practice area">
        {PRACTICE_AREAS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={practiceArea === value}
            className={practiceArea === value ? 'practice-tab active' : 'practice-tab'}
            onClick={() => choosePracticeArea(value)}
            disabled={isLoading}
            title={
              practiceAreaLocked && practiceArea !== value
                ? 'Starts a new conversation — practice area is fixed once a conversation begins'
                : 'Which body of authority to search'
            }
          >
            {label}
          </button>
        ))}
        {practiceAreaLocked && (
          <span className="practice-tabs-lock" title="Practice area is fixed for this conversation">
            locked for this conversation
          </span>
        )}
        <button
          type="button"
          className="practice-tabs-new"
          onClick={newChat}
          disabled={isLoading}
          title="Start a new conversation"
        >
          + New
        </button>
      </div>

      {/* ── Messages (identical structure to v1 ChatMessages) ── */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <h1 style={{ position: 'relative', zIndex: 1 }}>Whatcha want?</h1>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-content">
                  {message.role === 'user' ? (
                    <div className="user-bubble">{message.content}</div>
                  ) : message.content ? (
                    <>
                    {message.mode && (
                      <div className={`mode-badge mode-${message.mode}`}>
                        {PRACTICE_LABELS[message.mode] || message.mode}
                      </div>
                    )}
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children, ...props }) => {
                          const isAppDomain = href && (
                            href.includes('publicfinancetax.com') ||
                            href.includes('103lawbot.com') ||
                            href.includes('localhost')
                          );
                          if (href && !isAppDomain && (href.startsWith('http://') || href.startsWith('https://'))) {
                            return <a href={href} target="_blank" rel="noreferrer" {...props}>{children}</a>;
                          }
                          return <strong style={{ color: 'var(--primary-color)', cursor: 'default' }}>{children}</strong>;
                        },
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                    </>
                  ) : thinkingTool ? (
                    <>
                      <div className="typing-indicator">
                        <span></span><span></span><span></span>
                      </div>
                      <div className="v2chat-thinking-label">
                        {toolLabel(thinkingTool)}…
                      </div>
                    </>
                  ) : (
                    <div className="typing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* ── Input (identical structure to v1 ChatInput) ── */}
      {attachedFiles.length > 0 && (
        <div style={{
          padding: '8px 20px',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          {attachedFiles.map((f, idx) => (
            <span key={idx} style={{
              display: 'inline-block',
              marginRight: '8px',
              padding: '4px 8px',
              backgroundColor: 'var(--hover-color)',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
            }}>
              {f.name}
              <button
                onClick={() => removeAttachedFile(idx)}
                style={{
                  marginLeft: '6px',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontSize: '12px',
                }}
                title="Remove file"
              >
                x
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="input-container">
        <div className="input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            id="v2-file-input"
            multiple
            style={{ display: 'none' }}
            accept={ACCEPT}
            onChange={handleFileChange}
          />
          <label htmlFor="v2-file-input" className="upload-btn" title="Attach document">+</label>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Ask a question..."
            className="message-input"
            rows={1}
            disabled={isLoading}
          />
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={isLoading}
            className="model-select"
            title="Model for this query"
            aria-label="Model for this query"
          >
            <option value="sonnet5">Sonnet 5 (fast)</option>
            <option value="opus55">Opus 5.5 (standard)</option>
            <option value="fable51">Fable 5.1 (deep)</option>
          </select>
          <select
            value={effort}
            onChange={(e) => setEffort(e.target.value)}
            disabled={isLoading}
            className="effort-select"
            title="Reasoning effort for this query"
            aria-label="Reasoning effort for this query"
          >
            <option value="standard">Standard</option>
            <option value="thorough">Thorough</option>
            <option value="maximum">Maximum</option>
          </select>
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="send-button"
          >
            &gt;
          </button>
        </div>
        {EFFORT_HINTS[effort] && (
          <div className="effort-hint">{EFFORT_HINTS[effort](model)}</div>
        )}
      </div>

    </>
  );
};

export default V2Chat;
